<template>
<div class="drill-card">
  <!-- STORY-002 §8: outside the status branches so the marker keeps
       sweeping through speak and feedback. -->
  <drill-timeline
    v-if="showTimeline"
    :spans="timelineSpans"
    :progress="progress"
    :pulsing="drill.status === 'speak'"
    :no-transition="timelineNoTransition"
    :playback-rate="playbackRate"
  />

  <div v-if="drill.status === 'loading'" class="drill-state drill-state--center">
    <i class="fas fa-circle-notch fa-spin drill-spinner"></i>
    <p class="drill-message">{{ loadingStalled ? 'Still fetching subtitles…' : 'Loading subtitles…' }}</p>
  </div>

  <div v-else-if="drill.status === 'unsupported'" class="drill-state drill-state--center">
    <p class="drill-message">{{ unsupportedMessage }}</p>
  </div>

  <!-- Challenge arrival replaces the ready card: one thing to read, one to tap. -->
  <div v-else-if="drill.status === 'ready' && activeChallenge" class="drill-state drill-state--challenge">
    <p class="challenge-from">
      <i class="fas fa-bolt"></i>
      <template v-if="activeChallenge.score !== null">
        {{ challengerName }} scored {{ activeChallenge.score }}% on this line
      </template>
      <template v-else>{{ challengerName }} sent you this line</template>
    </p>

    <p v-if="sentence" class="challenge-sentence">{{ sentence.text }}</p>
    <p v-if="subtitle" class="drill-subtitle-text">{{ subtitle }}</p>

    <button
      v-if="activeChallenge.audioId"
      type="button"
      class="btn btn-secondary btn-sm"
      @click="$emit('hear-challenge')"
    >
      <i class="fas fa-volume-up"></i>
      {{ playingChallenge ? 'Playing…' : 'Hear their attempt' }}
    </button>

    <p class="challenge-coach">Tap Play, listen, then say it back.</p>
  </div>

  <div v-else-if="drill.status === 'ready'" class="drill-state drill-state--ready">
    <p v-if="videoTitle" class="drill-video-title">{{ videoTitle }}</p>
    <p class="drill-hint">Tap play to start</p>
    <span v-if="drill.sentenceCount > 0" class="sentence-chip">{{ drill.sentenceIndex + 1 }}/{{ drill.sentenceCount }}</span>
  </div>

  <div v-else-if="drill.status === 'listen'" class="drill-state drill-state--listen">
    <div class="drill-header-row">
      <h4>{{ drill.cruising ? 'Playing…' : 'Listen…' }}</h4>
      <span v-if="drill.sentenceCount > 0" class="sentence-chip">{{ drill.sentenceIndex + 1 }}/{{ drill.sentenceCount }}</span>
    </div>
    <!-- STORY-002 §3: intro previews the upcoming line; outro has nothing left. -->
    <div v-if="drill.cruising" class="drill-sentence-block drill-cruise-block">
      <p class="drill-cruise-hint">
        <i class="fas fa-volume-up"></i>
        {{ cruisingToOutro ? 'Playing the rest of the clip…' : 'Playing…' }}
      </p>
      <p v-if="!cruisingToOutro && sentence" class="drill-subtitle-text">Coming up: &ldquo;{{ sentence.text }}&rdquo;</p>
    </div>
    <div v-else class="drill-sentence-block">
      <p class="drill-sentence-text">{{ sentence ? sentence.text : '' }}</p>
      <p v-if="subtitle" class="drill-subtitle-text">{{ subtitle }}<span v-if="subtitleSource === 'machine'" class="drill-subtitle-tag">auto-translated</span></p>
    </div>
    <!-- Clips too long for the timeline keep the plain progress bar. -->
    <div v-if="!showTimeline" class="progress-track">
      <div class="progress-track__fill" :style="{ width: (progress * 100) + '%' }"></div>
    </div>
  </div>

  <!-- speak covers mic live, scoring and failed attempt (flags, not statuses) -->
  <div v-else-if="drill.status === 'speak'" class="drill-state drill-state--speak">
    <div class="drill-header-row">
      <h4>{{ drill.scoring ? 'Scoring your attempt' : 'Your turn to speak' }}</h4>
      <i v-if="drill.scoring" class="fas fa-circle-notch fa-spin drill-scoring-spinner"></i>
    </div>

    <!-- The only submit control. The ring drains over the controller's own cap
         timer; no drain circle when nothing is timed (stalled mic). -->
    <div v-if="!drill.scoring" class="drill-speak-ring">
      <svg :key="speakAttemptKey" class="drill-speak-ring__svg" viewBox="0 0 120 120" aria-hidden="true">
        <circle class="drill-speak-ring__track" cx="60" cy="60" r="54"></circle>
        <circle
          v-if="speakCapMs"
          class="drill-speak-ring__drain"
          cx="60"
          cy="60"
          r="54"
          :style="{ '--speak-cap': speakCapMs + 'ms' }"
        ></circle>
      </svg>
      <!-- A check, not a mic: a mic glyph read as a status light, not a button. -->
      <button type="button" class="drill-speak-ring__done" aria-label="Done speaking" @click="$emit('end-attempt')">
        <i class="fas fa-check"></i>
        <span>Done</span>
      </button>
    </div>

    <mic-level-meter v-if="!drill.scoring" />

    <p v-if="subtitle" class="drill-subtitle-text">{{ subtitle }}<span v-if="subtitleSource === 'machine'" class="drill-subtitle-tag">auto-translated</span></p>

    <p class="drill-recognized-text drill-recognized-text--empty">
      {{ drill.scoring ? 'Listening back…' : 'Speak now. Pause, or tap when you\'re done' }}
    </p>

    <!-- Information only. The mic is still open while these show, so the copy
         says so; SPEECH_DETECTED clears them. -->
    <div v-if="drill.asrError" class="drill-empty-hint">
      <p>{{ asrErrorMessage }}</p>
      <button type="button" class="btn btn-ghost btn-sm accent-link" @click.stop="$emit('report-problem')">
        <i class="fas fa-bug"></i> Report a problem
      </button>
    </div>
    <div v-else-if="drill.micStalled" class="drill-empty-hint">
      <p>Still not hearing anything. Check that your mic is working.</p>
    </div>
    <div v-else-if="drill.showEmptyHint" class="drill-empty-hint">
      <p>Didn't catch that, still listening.</p>
    </div>
  </div>

  <div v-else-if="drill.status === 'feedback'" class="drill-state drill-state--feedback" @click="onFeedbackCardClick">
    <!-- Above the score: the comparison is why they followed the link. -->
    <div
      v-if="activeChallenge && activeChallenge.score !== null && scoreResult"
      class="challenge-versus"
      :class="{ 'challenge-versus--won': beatTheChallenge }"
    >
      <span class="challenge-versus-line">
        You {{ scoreResult.accuracy }}%
        <span class="challenge-versus-sep">vs</span>
        {{ challengerName }} {{ activeChallenge.score }}%
      </span>
      <strong v-if="beatTheChallenge">You beat {{ challengerName }}!</strong>
      <strong v-else-if="challengeGap > 0">{{ challengeGap }} to go</strong>
      <strong v-else>Dead heat</strong>
      <button type="button" class="btn btn-ghost btn-sm accent-link" @click.stop="$emit('share')">
        <i class="fas fa-share-alt"></i> Send your score back
      </button>
    </div>

    <div class="score-hero-row" v-if="scoreResult && scoreResult.accuracy !== null">
      <span class="score-hero-number" :class="scoreHeroClass">{{ scoreResult.accuracy }}%</span>
      <div class="score-hero-label">
        <strong>{{ scoreResult.accuracy >= 80 ? 'Nice! Moving on…' : 'Keep going' }}</strong>
        <span v-if="drill.replaying || drill.replayPending">Listening to original</span>
        <span v-else-if="drill.autoAdvancing">Next in a moment - tap to stay</span>
        <span v-else-if="drill.autoRetrying">Retrying in a moment - tap to stay</span>
        <span v-else-if="scoreResult.accuracy < 80">Retry or skip to next</span>
      </div>
    </div>
    <score-panel status="ready" :score-result="scoreResult" :recognized-text="recognizedText" />
    <p v-if="subtitle" class="drill-subtitle-text">{{ subtitle }}<span v-if="subtitleSource === 'machine'" class="drill-subtitle-tag">auto-translated</span></p>
    <p v-if="drill.replaying" class="drill-replaying-note"><i class="fas fa-volume-up"></i> Playing original&hellip;</p>
    <div v-else-if="!drill.replayPending" class="drill-hear-row">
      <button type="button" class="btn btn-ghost btn-sm" @click.stop="$emit('hear-again')"><i class="fas fa-volume-up"></i> Hear it again</button>
      <button
        v-if="hasRecording"
        type="button"
        class="btn btn-ghost btn-sm"
        @click.stop="$emit('hear-self')"
      ><i class="fas fa-user"></i> {{ playingSelf ? 'Playing…' : 'Hear yourself' }}</button>
      <!-- @click.stop, or onFeedbackCardClick cancels the countdown too;
           LearningScreen cancels it deliberately on open. -->
      <button
        v-if="scoreResult && scoreResult.accuracy >= 80 && !activeChallenge"
        type="button"
        class="btn btn-ghost btn-sm accent-link"
        @click.stop="$emit('share')"
      ><i class="fas fa-share-alt"></i> Share this win</button>
    </div>
  </div>

  <div v-else-if="drill.status === 'micBlocked'" class="drill-state drill-state--center">
    <p class="drill-message">EZ Shadow needs your microphone to hear you practice.</p>
  </div>

  <div v-else-if="drill.status === 'done'" class="drill-state drill-state--done">
    <div class="done-icon"><i class="fas fa-flag-checkered"></i></div>
    <div class="done-stat">{{ summary.practicedCount }}</div>
    <div class="done-stat-label">sentence{{ summary.practicedCount === 1 ? '' : 's' }} practiced</div>
    <template v-if="summary.avgAccuracy !== null">
      <div class="done-stat done-stat--accuracy">{{ summary.avgAccuracy }}%</div>
      <div class="done-stat-label">average accuracy</div>
    </template>
  </div>
</div>
</template>

<script>
import ScorePanel from './ScorePanel.vue';
import MicLevelMeter from './MicLevelMeter.vue';
import DrillTimeline from './DrillTimeline.vue';
import { sessionSummary, ACTIVE_STATUSES as TIMELINE_STATUSES } from '../../services/drill';

// STORY-002 §8b: ~4px per area on a 360px viewport. Arithmetic, not
// thumb-tested; re-tune on a real phone.
const FULL_BAR_MAX_AREAS = 90;

export default {
  name: 'DrillCard',
  components: {
    ScorePanel,
    MicLevelMeter,
    DrillTimeline
  },
  props: {
    drill: {
      type: Object,
      required: true
    },
    loadingStalled: {
      type: Boolean,
      default: false
    },
    sentence: {
      type: Object,
      default: null
    },
    // Full clip; only the timeline reads this.
    sentences: {
      type: Array,
      default: () => []
    },
    scoreResult: {
      type: Object,
      default: null
    },
    recognizedText: {
      type: String,
      default: ''
    },
    videoTitle: {
      type: String,
      default: ''
    },
    progress: {
      type: Number,
      default: 0
    },
    playbackRate: {
      type: Number,
      default: 1
    },
    timelineNoTransition: {
      type: Boolean,
      default: false
    },
    subtitle: {
      type: String,
      default: ''
    },
    subtitleSource: {
      type: String,
      default: null
    },
    hasRecording: {
      type: Boolean,
      default: false
    },
    playingSelf: {
      type: Boolean,
      default: false
    },
    // Share-link context, deliberately not drill state.
    challenge: {
      type: Object,
      default: null
    },
    playingChallenge: {
      type: Boolean,
      default: false
    },
    // Controller's cap duration; null means nothing is timed.
    speakCapMs: {
      type: Number,
      default: null
    },
    // Keys the ring <svg> so the CSS animation restarts per attempt.
    speakAttemptKey: {
      type: Number,
      default: 0
    }
  },
  computed: {
    // Only while still on the challenged sentence; after that its claims
    // would be about a sentence the sender never attempted.
    activeChallenge() {
      if (!this.challenge) return null;
      return this.challenge.sentenceIndex === this.drill.sentenceIndex ? this.challenge : null;
    },
    challengerName() {
      const c = this.activeChallenge;
      return (c && c.name) || 'Your friend';
    },
    // Same test as drill.js CRUISE_ARRIVED: already practiced = outro.
    cruisingToOutro() {
      return this.drill.practicedIndices.indexOf(this.drill.sentenceIndex) !== -1;
    },
    // STORY-002 §8: dense clips get NO timeline (owner rejected the windowed
    // fallback), so in practice this is a short-clip feature.
    showTimeline() {
      return this.sentences.length > 0 &&
        this.sentences.length <= FULL_BAR_MAX_AREAS &&
        TIMELINE_STATUSES.indexOf(this.drill.status) !== -1;
    },
    timelineSpans() {
      return this.showTimeline ? this.buildFullSpans() : [];
    },
    beatTheChallenge() {
      const c = this.activeChallenge;
      if (!c || c.score === null || !this.scoreResult) return false;
      return this.scoreResult.accuracy > c.score;
    },
    challengeGap() {
      const c = this.activeChallenge;
      if (!c || c.score === null || !this.scoreResult) return 0;
      return c.score - this.scoreResult.accuracy;
    },
    asrErrorMessage() {
      switch (this.drill.asrError) {
        // The mic is still open, so recoverable cases say so. Quota errors
        // don't invite a retry: scoring is gone for the day.
        case 'TIMEOUT': return 'Scoring took too long. Still listening, so try that sentence again.';
        case 'RATE_LIMITED': return 'Too many attempts in a row. Wait a minute, then try again.';
        case 'QUOTA_EXHAUSTED': return "Today's online scoring is used up (resets at midnight UTC). For English, switch Settings > Speech recognition to Local model to keep going.";
        case 'TOO_LARGE': return 'That recording was too long to score. Still listening, so try a shorter take.';
        case 'NETWORK': return "Couldn't reach the scoring service. Check your connection, then try again.";
        default: return "Couldn't score that attempt. Still listening, so try again.";
      }
    },
    unsupportedMessage() {
      if (this.drill.errorCode === 'NO_TRANSCRIPT') {
        return "This video doesn't have usable English subtitles, so it can't be scored. Pick another video.";
      }
      return "Couldn't load subtitles for this video.";
    },
    summary() {
      return sessionSummary(this.drill);
    },
    scoreHeroClass() {
      var acc = this.scoreResult && this.scoreResult.accuracy;
      if (acc >= 85) return 'good';
      if (acc >= 60) return 'ok';
      return 'poor';
    }
  },
  methods: {
    spanKind(index) {
      if (index === this.drill.sentenceIndex) return 'active';
      if (this.drill.practicedIndices.indexOf(index) !== -1) return 'practiced';
      return 'upcoming';
    },
    // 0%/100% are the first sentence start and last sentence end: no video
    // duration is exposed.
    buildFullSpans() {
      const sentences = this.sentences;
      const totalStart = sentences[0].start;
      const totalEnd = sentences[sentences.length - 1].end;
      const span = Math.max(totalEnd - totalStart, 0.001);
      return sentences.map((s, i) => ({
        startPct: (s.start - totalStart) / span * 100,
        widthPct: Math.max((s.end - s.start) / span * 100, 0),
        kind: this.spanKind(i)
      }));
    },
    onFeedbackCardClick() {
      if (this.drill.autoAdvancing || this.drill.autoRetrying) {
        this.$emit('card-tap');
      }
    }
  }
};
</script>

<style scoped>
.drill-card {
  background: var(--surface);
  color: var(--text);
  border-radius: var(--r-card);
  margin: 0 12px;
  padding: 16px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  position: relative;
}

.drill-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.drill-state--center,
.drill-state--ready,
.drill-state--challenge,
.drill-state--done {
  align-items: center;
  justify-content: center;
  text-align: center;
}

.drill-state--center { gap: 12px; }
.drill-state--ready { gap: 8px; }
.drill-state--challenge { gap: 10px; }
.drill-state--done { gap: 2px; }

.drill-message {
  color: var(--muted);
  font-size: 15px;
  line-height: 1.45;
  margin: 0;
  max-width: 32ch;
}

.drill-spinner {
  font-size: 22px;
  color: var(--accent);
}

.drill-video-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--text);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.drill-hint {
  margin: 0;
  font-size: 14px;
  color: var(--muted);
}

.challenge-from {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--accent);
}

.challenge-from i {
  margin-right: 4px;
}

.challenge-sentence {
  margin: 0;
  font-size: 18px;
  line-height: 1.45;
  font-weight: 600;
  color: var(--text);
}

.challenge-coach {
  margin: 0;
  font-size: 13px;
  color: var(--faint);
}

.drill-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex: none;
}

.drill-header-row h4 {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}

.sentence-chip {
  font-size: 12px;
  font-weight: 700;
  color: var(--muted);
  background: var(--field);
  border-radius: var(--r-pill);
  padding: 2px 10px;
  font-variant-numeric: tabular-nums;
}

.drill-sentence-block {
  flex: 1;
  margin: 10px 0;
  overflow-y: auto;
  min-height: 0;
}

.drill-sentence-text {
  margin: 0 0 6px;
  font-size: 19px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--text);
}

.drill-subtitle-text {
  margin: 0 0 6px;
  font-size: 14px;
  line-height: 1.45;
  color: var(--muted);
}

.drill-subtitle-tag {
  font-size: 11px;
  color: var(--faint);
  background: var(--field);
  border-radius: var(--r-pill);
  padding: 1px 8px;
  margin-left: 6px;
  white-space: nowrap;
}

.drill-cruise-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 6px;
}

.drill-cruise-hint {
  margin: 0;
  font-size: 15px;
  color: var(--text);
}

.drill-cruise-hint i {
  color: var(--accent);
  margin-right: 6px;
}

.progress-track {
  height: 4px;
  border-radius: var(--r-pill);
  background: var(--field);
  overflow: hidden;
  flex: none;
}

.progress-track__fill {
  height: 100%;
  background: var(--accent);
  transition: width 0.2s linear;
}

/* Sized off a 120-unit viewBox so it scales with one width. */
.drill-speak-ring {
  position: relative;
  width: 132px;
  height: 132px;
  margin: 8px auto 12px;
  flex: none;
}

.drill-speak-ring__svg {
  width: 100%;
  height: 100%;
  display: block;
}

.drill-speak-ring__track {
  fill: none;
  stroke: var(--field);
  stroke-width: 6;
}

/* Duration comes in as a CSS var, no JS tick; the <svg> key restarts it. */
.drill-speak-ring__drain {
  fill: none;
  stroke: var(--accent);
  stroke-width: 6;
  stroke-linecap: round;
  stroke-dasharray: 339.29;            /* 2π × r(54), the full circumference */
  transform: rotate(-90deg);           /* start the gap at 12 o'clock */
  transform-origin: 50% 50%;
  animation: drill-ring-drain var(--speak-cap, 6000ms) linear forwards;
}

@keyframes drill-ring-drain {
  from { stroke-dashoffset: 0; }
  to   { stroke-dashoffset: 339.29; }
}

/* Green, not gold: gold would merge with the ring. Dark ink on the dark-tuned
   green keeps 11px bold text well above AA. */
.drill-speak-ring__done {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 88px;
  height: 88px;
  border-radius: var(--r-pill);
  background: var(--pass);
  color: var(--pass-ink);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  transition: filter 0.18s ease, transform 0.18s ease;
}

/* Instant press, as ActionBar's buttons. */
.drill-speak-ring__done:active {
  filter: brightness(0.88);
  transform: translate(-50%, -50%) scale(0.94);
  transition-duration: 0s;
}

.drill-speak-ring__done i {
  font-size: 26px;
}

.drill-speak-ring__done span {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.drill-recognized-text {
  flex: 1;
  margin: 10px 0;
  font-size: 15px;
  line-height: 1.5;
  color: var(--faint);
  overflow-y: auto;
}

.drill-scoring-spinner {
  color: var(--accent);
  font-size: 15px;
}

.drill-empty-hint {
  margin-top: 8px;
  padding-top: 10px;
  border-top: 1px solid var(--line);
  text-align: center;
}

.drill-empty-hint p {
  margin: 0;
  font-size: 13px;
  color: var(--muted);
}

.drill-state--feedback {
  cursor: default;
  gap: 4px;
}

.score-hero-row {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 10px;
  flex: none;
}

.score-hero-number {
  font-size: 40px;
  font-weight: 800;
  letter-spacing: -1px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.score-hero-number.good { color: var(--pass); }
.score-hero-number.ok   { color: var(--warn); }
.score-hero-number.poor { color: var(--fail); }

.score-hero-label {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.score-hero-label strong {
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
}

.score-hero-label span {
  font-size: 13px;
  color: var(--muted);
}

.drill-replaying-note {
  align-self: center;
  margin: 6px 0 0;
  font-size: 13px;
  color: var(--muted);
  text-align: center;
}

.drill-hear-row {
  display: flex;
  align-self: center;
  gap: 4px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 4px;
}

.accent-link { color: var(--accent); }
.accent-link:hover { color: var(--accent); background: var(--accent-soft); }

.challenge-versus {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 10px 12px;
  margin-bottom: 10px;
  border-radius: var(--r-control);
  background: var(--accent-soft);
}

.challenge-versus--won {
  background: var(--pass-soft);
}

.challenge-versus-line {
  font-size: 14px;
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

.challenge-versus-sep {
  color: var(--faint);
  margin: 0 4px;
}

.challenge-versus strong {
  font-size: 15px;
  color: var(--text);
}

.done-icon {
  width: 56px;
  height: 56px;
  border-radius: var(--r-pill);
  background: var(--accent-soft);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  margin-bottom: 10px;
}

.done-stat {
  font-size: 28px;
  font-weight: 800;
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

.done-stat--accuracy {
  color: var(--pass);
  margin-top: 8px;
}

.done-stat-label {
  font-size: 13px;
  color: var(--muted);
}

@media (max-height: 600px) {
  .drill-card {
    min-height: 160px;
  }
}
</style>
